import { z } from "zod";
import type { TaskManualTestsGroupDTO } from "@project/shared";
import { defineTool } from "../harness/index.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import { textResult } from "./helpers.js";

export function buildListManualTestsTool(connection: AgentConnection) {
  return defineTool(
    "list_manual_tests",
    "List the manual test checklist items for the current task. Use to see what manual verification steps have already been recorded.",
    {},
    async () => {
      try {
        const items = await connection.call("listManualTests", {
          sessionId: connection.sessionId,
        });
        if (items.length === 0) return textResult("No manual tests recorded for this task.");
        const lines = items.flatMap((item, i) => {
          const checked = item.checked ? "[x]" : "[ ]";
          const row = [`${i + 1}. ${checked} ${item.title}`];
          for (const f of item.failures ?? []) {
            const who = f.userName ?? "Someone";
            const reason = f.reason ?? "(no message)";
            row.push(`   ⚠ Failed (${who}): ${reason}`);
          }
          return row;
        });
        return textResult(lines.join("\n"));
      } catch {
        return textResult("Failed to list manual tests.");
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

/** Compact grouped rendering for cross-task manual-test queries: one header per
 * task, one line per test with a status glyph, and the failing reason indented
 * under each rejected test. */
function renderManualTestGroups(groups: TaskManualTestsGroupDTO[]): string {
  const lines: string[] = [];
  for (const g of groups) {
    lines.push(`## ${g.title} [${g.status}] (${g.slug})`);
    for (const t of g.tests) {
      const mark = t.status === "approved" ? "✓" : t.status === "rejected" ? "✗" : "○";
      lines.push(`  ${mark} ${t.title} — ${t.status}`);
      if (t.status === "rejected") {
        for (const f of t.failures ?? []) {
          lines.push(`      ⚠ ${f.userName ?? "Someone"}: ${f.reason ?? "(no message)"}`);
        }
      }
    }
    lines.push("");
  }
  return lines.join("\n").trimEnd();
}

export function buildQueryManualTestsTool(connection: AgentConnection) {
  return defineTool(
    "query_manual_tests",
    "Query manual tests across many tasks in this project, grouped by task. Filter by card status (ReviewDev, ReviewLive, Complete, ...) and/or test status (open | approved | rejected). Use to answer 'show all OPEN manual tests in ReviewDev' or 'show all REJECTED manual tests with the failing reason'. With no filters it defaults to the needs-attention view: open+rejected tests on ReviewDev/ReviewLive cards.",
    {
      cardStatuses: z
        .array(z.string())
        .optional()
        .describe('Filter tasks by card status, e.g. ["ReviewDev", "ReviewLive"]'),
      testStatuses: z
        .array(z.enum(["open", "approved", "rejected"]))
        .optional()
        .describe("Filter tests by status: open | approved | rejected"),
    },
    async ({ cardStatuses, testStatuses }) => {
      try {
        const groups = await connection.call("queryManualTests", {
          sessionId: connection.sessionId,
          cardStatuses,
          testStatuses,
        });
        if (groups.length === 0) return textResult("No manual tests match those filters.");
        return textResult(renderManualTestGroups(groups));
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Unknown error";
        return textResult(`Failed to query manual tests: ${msg}`);
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

export function buildSetManualTestsTool(connection: AgentConnection) {
  return defineTool(
    "set_manual_tests",
    "Add manual test steps to the task checklist. Existing items with the same title are automatically skipped (deduplication). Use to record specific manual verification steps that reviewers should follow when testing this PR.",
    {
      items: z
        .array(z.object({ title: z.string().min(1).describe("A concise, actionable test step") }))
        .min(1)
        .describe("List of manual test steps to add"),
    },
    async ({ items }) => {
      try {
        const result = await connection.call("setManualTests", {
          sessionId: connection.sessionId,
          items,
        });
        const parts = [`Created ${result.created} manual test item(s).`];
        if (result.skipped > 0) parts.push(`Skipped ${result.skipped} duplicate(s).`);
        return textResult(parts.join(" "));
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Unknown error";
        return textResult(`Failed to set manual tests: ${msg}`);
      }
    },
  );
}

export function buildEditManualTestTool(connection: AgentConnection) {
  return defineTool(
    "edit_manual_test",
    "Rename an existing manual test step. Identify the test by its current title (case-insensitive); pass the new title to replace it. Use to correct or refine a recorded manual verification step.",
    {
      title: z.string().min(1).describe("The current title of the manual test to edit"),
      newTitle: z.string().min(1).describe("The new title for the manual test"),
    },
    async ({ title, newTitle }) => {
      try {
        await connection.call("editManualTest", {
          sessionId: connection.sessionId,
          title,
          newTitle,
        });
        return textResult(`Updated manual test to "${newTitle}".`);
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Unknown error";
        return textResult(`Failed to edit manual test: ${msg}`);
      }
    },
  );
}

export function buildRemoveManualTestTool(connection: AgentConnection) {
  return defineTool(
    "remove_manual_test",
    "Remove an existing manual test step from the task checklist. Identify the test by its title (case-insensitive). Use to delete a stale or incorrect manual verification step.",
    {
      title: z.string().min(1).describe("The title of the manual test to remove"),
    },
    async ({ title }) => {
      try {
        await connection.call("removeManualTest", {
          sessionId: connection.sessionId,
          title,
        });
        return textResult(`Removed manual test "${title}".`);
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Unknown error";
        return textResult(`Failed to remove manual test: ${msg}`);
      }
    },
  );
}

export function buildApproveManualTestTool(connection: AgentConnection) {
  return defineTool(
    "approve_manual_test",
    "Sign off on (approve) a manual test step on behalf of your authenticated user. Identify the test by its title (case-insensitive). Use after you have verified the step passes.",
    {
      title: z.string().min(1).describe("The title of the manual test to approve"),
    },
    async ({ title }) => {
      try {
        await connection.call("approveManualTest", {
          sessionId: connection.sessionId,
          title,
        });
        return textResult(`Approved manual test "${title}".`);
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Unknown error";
        return textResult(`Failed to approve manual test: ${msg}`);
      }
    },
  );
}

export function buildRejectManualTestTool(connection: AgentConnection) {
  return defineTool(
    "reject_manual_test",
    "Flag an issue with (reject) a manual test step on behalf of your authenticated user, recording the reason. Identify the test by its title (case-insensitive). Use when the step fails verification.",
    {
      title: z.string().min(1).describe("The title of the manual test to reject"),
      reason: z
        .string()
        .min(1)
        .max(2000)
        .describe("Why the test failed — what went wrong, shown to the team"),
    },
    async ({ title, reason }) => {
      try {
        await connection.call("rejectManualTest", {
          sessionId: connection.sessionId,
          title,
          reason,
        });
        return textResult(`Flagged an issue with manual test "${title}": ${reason}`);
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Unknown error";
        return textResult(`Failed to reject manual test: ${msg}`);
      }
    },
  );
}
